import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { aiApi } from '@/api/ai'
import type { AiUsage } from '@/types/ai'

export const AI_USAGE_QUERY_KEY = ['ai-usage'] as const

export function useAiCredits() {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: AI_USAGE_QUERY_KEY,
    queryFn: () => aiApi.getUsage(),
    staleTime: 5_000,
  })

  const applyDebit = useCallback(
    (cost: number) => {
      queryClient.setQueryData<AiUsage>(AI_USAGE_QUERY_KEY, (old) => {
        if (!old || old.creditsLimit < 0) return old
        const creditsUsed = Math.min(old.creditsLimit, old.creditsUsed + cost)
        const creditsRemaining = Math.max(0, old.creditsLimit - creditsUsed)
        return { ...old, creditsUsed, creditsRemaining }
      })
    },
    [queryClient]
  )

  const syncUsage = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: AI_USAGE_QUERY_KEY })
  }, [queryClient])

  /** Update the counter immediately, then reconcile with the server. */
  const recordCharge = useCallback(
    async (cost: number) => {
      applyDebit(cost)
      await syncUsage()
    },
    [applyDebit, syncUsage]
  )

  return {
    usage: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    applyDebit,
    syncUsage,
    recordCharge,
  }
}
